import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { AdvancePaymentConfirmationRequestViewModel } from '../models/AdvancePaymentConfirmationRequestViewModel';
import type { ChippinStartShoppingRequestViewModel } from '../models/ChippinStartShoppingRequestViewModel';
import type { ChippinTransactionStatusViewModel } from '../models/ChippinTransactionStatusViewModel';
import type { ConfirmPaymentModel } from '../models/ConfirmPaymentModel';
import type { ConfirmPaymentRequestViewModel } from '../models/ConfirmPaymentRequestViewModel';
import type { EmptyResponseViewModel } from '../models/EmptyResponseViewModel';
import type { GarantiPayConfirmPaymentRequestViewModel } from '../models/GarantiPayConfirmPaymentRequestViewModel';
import type { GarantiPayConfirmPaymentViewModel } from '../models/GarantiPayConfirmPaymentViewModel';
import type { GarantiPaySecurePaymentRequestViewModel } from '../models/GarantiPaySecurePaymentRequestViewModel';
import type { GarantiPaySecurePaymentViewModel } from '../models/GarantiPaySecurePaymentViewModel';
import type { GetParoOrGiftCardPointViewModel } from '../models/GetParoOrGiftCardPointViewModel';
import type { GetSeturCardViewModel } from '../models/GetSeturCardViewModel';
import type { HopiCompleteCoinTransactionRequestViewModel } from '../models/HopiCompleteCoinTransactionRequestViewModel';
import type { HopiGetBirdUserInfoViewModel } from '../models/HopiGetBirdUserInfoViewModel';
import type { HopiStartCoinTransactionRequestViewModel } from '../models/HopiStartCoinTransactionRequestViewModel';
import type { HopiStartCoinTransactionViewModel } from '../models/HopiStartCoinTransactionViewModel';
import type { HotelGetAdvancePaymentRequestViewModel } from '../models/HotelGetAdvancePaymentRequestViewModel';
import type { HotelGetAdvancePaymentViewModel } from '../models/HotelGetAdvancePaymentViewModel';
import type { IVRCreditCardViewModel } from '../models/IVRCreditCardViewModel';
import type { NonSecurePaymentRequestViewModel } from '../models/NonSecurePaymentRequestViewModel';
import type { NonSecurePaymentViewModel } from '../models/NonSecurePaymentViewModel';
import type { PaymentInstallmentViewModel } from '../models/PaymentInstallmentViewModel';
import type { PaymentRequestViewModel } from '../models/PaymentRequestViewModel';
import type { PaymentResultViewModel } from '../models/PaymentResultViewModel';
import type { PayWithParoOrGiftCardRequestViewModel } from '../models/PayWithParoOrGiftCardRequestViewModel';
import type { PayWithParoOrGiftCardViewModel } from '../models/PayWithParoOrGiftCardViewModel';
import type { PayWithSeturCardRequestViewModel } from '../models/PayWithSeturCardRequestViewModel';
import type { PointQueryRequestViewModel } from '../models/PointQueryRequestViewModel';
import type { PointsViewModel } from '../models/PointsViewModel';
import type { PointUsingRequestViewModel } from '../models/PointUsingRequestViewModel';
import type { SecurePaymentRequestViewModel } from '../models/SecurePaymentRequestViewModel';
import type { SecurePaymentViewModel } from '../models/SecurePaymentViewModel';
import type { SeturCardSendApproveCodeRequestViewModel } from '../models/SeturCardSendApproveCodeRequestViewModel';
import type { StartPaymentViewModel } from '../models/StartPaymentViewModel';
import type { WorldPayConfirmPaymentRequestViewModel } from '../models/WorldPayConfirmPaymentRequestViewModel';
import type { WorldPayConfirmPaymentViewModel } from '../models/WorldPayConfirmPaymentViewModel';
import type { WorldPayGenerateTokenRequestViewModel } from '../models/WorldPayGenerateTokenRequestViewModel';
import type { WorldPayGenerateTokenViewModel } from '../models/WorldPayGenerateTokenViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class PaymentsService {
  /**
   * Start
   * Start payment
   * @returns StartPaymentViewModel Success
   * @throws ApiError
   */
  public static async startPayment(
    {
      basketid,
    }: {
      basketid: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<StartPaymentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<StartPaymentViewModel>({
      method: 'GET',
      url: `/payments/start`,
      headers: {
        ...(basketid ? { basketid: basketid } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Finish
   * Finish payment
   * @returns PaymentResultViewModel Success
   * @throws ApiError
   */
  public static async finishPayment(
    {
      b,
      t,
    }: {
      /**
       * Basket id
       */
      b?: string;
      /**
       * Payment token
       */
      t?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<PaymentResultViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<PaymentResultViewModel>>({
      method: 'GET',
      url: `/payments/finish`,
      query: {
        ...(b ? { b: b } : {}),
        ...(t ? { t: t } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Request
   * Makes a payment request
   * @returns PaymentRequestViewModel Success
   * @throws ApiError
   */
  public static async requestPayment(
    {
      paymentRequestId,
      authorization,
    }: {
      /**
       * Payment request id
       */
      paymentRequestId?: string;
      /**
       * Authorization
       */
      authorization?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<PaymentRequestViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<PaymentRequestViewModel>({
      method: 'GET',
      url: `/payments/request`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * GetIVRCreditCard
   * Get IVR Credit Card
   * @returns IVRCreditCardViewModel Success
   * @throws ApiError
   */
  public static async getIvrCreditCard(
    {
      basketId,
    }: {
      /**
       * BasketId
       */
      basketId: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<IVRCreditCardViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<IVRCreditCardViewModel>({
      method: 'GET',
      url: `/payments/ivrcreditcard`,
      query: {
        ...(basketId ? { basketId: basketId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Secure
   * Makes a secure payment
   * @returns SecurePaymentViewModel Success
   * @throws ApiError
   */
  public static async securePayment(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization?: string;
      body?: SecurePaymentRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SecurePaymentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SecurePaymentViewModel>({
      method: 'POST',
      url: `/payments/secure`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * CallCenterNonSecure
   * Makes a nonsecure payment
   * @returns NonSecurePaymentViewModel Success
   * @throws ApiError
   */
  public static async callCenterNonSecurePayment(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization?: string;
      body?: NonSecurePaymentRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<NonSecurePaymentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<NonSecurePaymentViewModel>({
      method: 'POST',
      url: `/payments/callcenter-nonsecure`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Confirm
   * Confirms the payment
   * @returns ConfirmPaymentModel Success
   * @throws ApiError
   */
  public static async confirmPayment(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization?: string;
      body?: ConfirmPaymentRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<ConfirmPaymentModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<ConfirmPaymentModel>({
      method: 'POST',
      url: `/payments/confirm`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Installments
   * Gets installment info
   * @returns PaymentInstallmentViewModel Success
   * @throws ApiError
   */
  public static async getInstallment(
    {
      paymentRequestId,
      amount,
      cardNo,
      installmentPostponement,
    }: {
      /**
       * Payment request id
       */
      paymentRequestId: string;
      /**
       * Amount
       */
      amount: number;
      /**
       * Card number
       */
      cardNo: string;
      installmentPostponement?: boolean;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<PaymentInstallmentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<PaymentInstallmentViewModel>({
      method: 'GET',
      url: `/payments/installments`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
        ...(amount ? { amount: amount } : {}),
        ...(cardNo ? { cardNo: cardNo } : {}),
        ...(installmentPostponement
          ? { installmentPostponement: installmentPostponement }
          : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Start (Chippin)
   * Starts shopping for chippin
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async chippinStartShopping(
    {
      body,
    }: {
      body?: ChippinStartShoppingRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/payments/chippin/start`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Status (Chippin)
   * Gets chippin payment status
   * @returns ChippinTransactionStatusViewModel Success
   * @throws ApiError
   */
  public static async chippinPaymentStatus(
    {
      paymentRequestId,
    }: {
      paymentRequestId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<ChippinTransactionStatusViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<ChippinTransactionStatusViewModel>({
      method: 'GET',
      url: `/payments/chippin/status`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Exit (Chippin)
   * Cashier exit for chippin
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async chippinCashierExit(
    {
      paymentRequestId,
    }: {
      paymentRequestId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/payments/chippin/exit`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Return (Chippin)
   * Returns shopping for chippin
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async chippinShoppingReturn(
    {
      paymentRequestId,
      paymentId,
    }: {
      paymentRequestId?: string;
      paymentId?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/payments/chippin/return`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
        ...(paymentId ? { paymentId: paymentId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Points (Paro)
   * Gets paro or gift cards points
   * @returns GetParoOrGiftCardPointViewModel Success
   * @throws ApiError
   */
  public static async getParoOrGiftCardsPoint(
    {
      paymentRequestId,
      paroNumber,
    }: {
      paymentRequestId?: string;
      paroNumber?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetParoOrGiftCardPointViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<GetParoOrGiftCardPointViewModel>({
      method: 'GET',
      url: `/payments/paro/points`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
        ...(paroNumber ? { paroNumber: paroNumber } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Start (Paro)
   * Pays with paro or gift card
   * @returns PayWithParoOrGiftCardViewModel Success
   * @throws ApiError
   */
  public static async payWithParoOrGiftCard(
    {
      body,
    }: {
      body?: PayWithParoOrGiftCardRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<PayWithParoOrGiftCardViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<PayWithParoOrGiftCardViewModel>({
      method: 'POST',
      url: `/payments/paro/start`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Cancel (Paro)
   * Cancels paro or gift card payment
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async cancelParoOrGiftCardPayment(
    {
      paymentRequestId,
      paymentId,
    }: {
      paymentRequestId?: string;
      paymentId?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/payments/paro/cancel`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
        ...(paymentId ? { paymentId: paymentId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Start (GarantiPay)
   * Secure payment with garanti pay
   * @returns GarantiPaySecurePaymentViewModel Success
   * @throws ApiError
   */
  public static async garantiPaySecurePayment(
    {
      body,
    }: {
      body?: GarantiPaySecurePaymentRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GarantiPaySecurePaymentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<GarantiPaySecurePaymentViewModel>({
      method: 'POST',
      url: `/payments/garantipay/start`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Confirm (GarantiPay)
   * Confirms payment for GarantiPay
   * @returns GarantiPayConfirmPaymentViewModel Success
   * @throws ApiError
   */
  public static async garantiPayConfirmPayment(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization?: string;
      body?: GarantiPayConfirmPaymentRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GarantiPayConfirmPaymentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<GarantiPayConfirmPaymentViewModel>({
      method: 'POST',
      url: `/payments/garantipay/confirm`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Token (WorldPay)
   * Generates token for WorldPay
   * @returns WorldPayGenerateTokenViewModel Success
   * @throws ApiError
   */
  public static async worldPayGenerateToken(
    {
      body,
    }: {
      body?: WorldPayGenerateTokenRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<WorldPayGenerateTokenViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<WorldPayGenerateTokenViewModel>({
      method: 'POST',
      url: `/payments/worldpay/token`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Confirm (WorldPay)
   * Confirms payment for WorldPay
   * @returns WorldPayConfirmPaymentViewModel Success
   * @throws ApiError
   */
  public static async worldPayConfirmPayment(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization?: string;
      body?: WorldPayConfirmPaymentRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<WorldPayConfirmPaymentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<WorldPayConfirmPaymentViewModel>({
      method: 'POST',
      url: `/payments/worldpay/confirm`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get (Hopi)
   * Get bird user info
   * @returns HopiGetBirdUserInfoViewModel Success
   * @throws ApiError
   */
  public static async hopiGetBirdUserInfo(
    {
      paymentRequestId,
      hopiToken,
    }: {
      paymentRequestId?: string;
      hopiToken?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HopiGetBirdUserInfoViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HopiGetBirdUserInfoViewModel>({
      method: 'GET',
      url: `/payments/hopi/get`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
        ...(hopiToken ? { hopiToken: hopiToken } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Start (Hopi)
   * Start coin transaction for Hopi
   * @returns HopiStartCoinTransactionViewModel Success
   * @throws ApiError
   */
  public static async hopiStartCoinTransaction(
    {
      body,
    }: {
      body?: HopiStartCoinTransactionRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HopiStartCoinTransactionViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HopiStartCoinTransactionViewModel>({
      method: 'POST',
      url: `/payments/hopi/start`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Complete (Hopi)
   * Complete coin transaction for Hopi
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async hopiCompleteCoinTransaction(
    {
      body,
    }: {
      body?: HopiCompleteCoinTransactionRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/payments/hopi/complete`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Refund (Hopi)
   * Refunds hopi coins
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async hopiRefundCoins(
    {
      paymentRequestId,
      paymentId,
    }: {
      paymentRequestId?: string;
      paymentId?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/payments/hopi/refund`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
        ...(paymentId ? { paymentId: paymentId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get (SeturCard)
   * Gets setur card balance
   * @returns GetSeturCardViewModel Success
   * @throws ApiError
   */
  public static async seturCardGet(
    {
      paymentRequestId,
      cardNo,
    }: {
      paymentRequestId?: string;
      cardNo?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetSeturCardViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<GetSeturCardViewModel>({
      method: 'GET',
      url: `/payments/seturcard/get`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
        ...(cardNo ? { cardNo: cardNo } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Send (SeturCard)
   * Sends otp code request for setur card
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async seturCardSend(
    {
      body,
    }: {
      body?: SeturCardSendApproveCodeRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/payments/seturcard/send`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Pay (SeturCard)
   * Pays with setur card
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async seturCardPay(
    {
      body,
    }: {
      body?: PayWithSeturCardRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/payments/seturcard/pay`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Advance Payment (Hotel)
   * Gets advance payment for hotel
   * @returns HotelGetAdvancePaymentViewModel Success
   * @throws ApiError
   */
  public static async advancePaymentHotel(
    {
      body,
    }: {
      body?: HotelGetAdvancePaymentRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelGetAdvancePaymentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HotelGetAdvancePaymentViewModel>({
      method: 'POST',
      url: `/payments/advance/hotel`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Confirm (Advance Payment)
   * Confirms advance payment
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async advancePaymentConfirm(
    {
      body,
    }: {
      body?: AdvancePaymentConfirmationRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/payments/advance/confirm`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get (Points)
   * Gets points for credit card
   * @returns PointsViewModel Success
   * @throws ApiError
   */
  public static async getPoints(
    {
      body,
    }: {
      body?: PointQueryRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<PointsViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<PointsViewModel>({
      method: 'POST',
      url: `/payments/points/get`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get (Expire Query)
   * Expire Query for credit card
   * @returns PointsViewModel Success
   * @throws ApiError
   */
  public static async getExpire(
    {
      body,
    }: {
      body?: PointQueryRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<PointsViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<PointsViewModel>({
      method: 'POST',
      url: `/payments/expire/get`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Pay (Points)
   * Pays points for credit card
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async payPoints(
    {
      body,
    }: {
      body?: PointUsingRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/payments/points/pay`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
